import { Box, Chip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

const PREVIEW_CAPTION = 'Example view'

function GuidePreviewFrame({ label = PREVIEW_CAPTION, children }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
        overflow: 'hidden',
        mb: 2,
      }}
    >
      {label ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1.5,
            py: 0.75,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
            color: 'text.secondary',
            fontWeight: 600,
            letterSpacing: 0.3,
          }}
        >
          {label}
        </Typography>
      ) : null}
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Box>
  )
}

function ActionCenterPreview() {
  const rows = [
    { label: 'Casting needs roster', campaign: 'Summer Launch', chip: 'Casting' },
    { label: 'Content ready for review', campaign: 'Brand Refresh', chip: 'Content' },
    { label: 'Client approval pending', campaign: 'Holiday Drop', chip: 'Approval' },
  ]
  return (
    <GuidePreviewFrame>
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {row.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.campaign}
            </Typography>
          </Box>
          <Chip label={row.chip} size="small" color="primary" variant="outlined" />
        </Box>
      ))}
    </GuidePreviewFrame>
  )
}

function QueueListPreview({ variant = 'campaigns' }) {
  const rows =
    variant === 'clients'
      ? [
          { primary: 'Acme Co.', secondary: '3 active campaigns' },
          { primary: 'Northwind', secondary: '1 active campaign' },
        ]
      : variant === 'clientQueues'
        ? [
            { primary: 'Roster approval', secondary: 'Summer Launch' },
            { primary: 'Content approval', secondary: 'Brand Refresh' },
          ]
        : variant === 'influencerQueues'
          ? [
              { primary: 'Invite: Summer Launch', secondary: 'Accept by Jun 12' },
              { primary: 'Campaign: Brand Refresh', secondary: '2 open tasks' },
            ]
          : [
              { primary: 'Summer Launch', secondary: 'Acme Co. · Casting' },
              { primary: 'Brand Refresh', secondary: 'Northwind · Content review' },
            ]
  return (
    <GuidePreviewFrame>
      {rows.map((row) => (
        <Box key={row.primary} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.primary}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.secondary}
          </Typography>
        </Box>
      ))}
    </GuidePreviewFrame>
  )
}

function PhaseStripPreview({ variant = 'am' }) {
  const phases =
    variant === 'client'
      ? [
          { name: 'Casting', detail: 'Approve creators' },
          { name: 'Content review', detail: 'Approve content' },
        ]
      : [
          { name: 'Casting', detail: 'Propose roster' },
          { name: 'Pipeline', detail: 'Track creators' },
          { name: 'Content review', detail: 'Release to client' },
        ]

  return (
    <GuidePreviewFrame>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {phases.map((phase, index) => (
          <Box
            key={phase.name}
            sx={{
              flex: '1 1 120px',
              p: 1.25,
              borderRadius: 1.5,
              border: (t) => `1px solid ${index === 0 ? t.palette.primary.main : t.palette.divider}`,
              bgcolor: (t) => (index === 0 ? alpha(t.palette.primary.main, 0.08) : 'transparent'),
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Phase {index + 1}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {phase.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {phase.detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </GuidePreviewFrame>
  )
}

function CastingRosterPreview() {
  return (
    <GuidePreviewFrame>
      {['Alex Rivera', 'Jordan Lee'].map((name) => (
        <Box
          key={name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          <Chip label="Proposed" size="small" variant="outlined" />
        </Box>
      ))}
    </GuidePreviewFrame>
  )
}

function PipelineColumnsPreview() {
  const cols = [
    { title: 'Invited', count: 2 },
    { title: 'In progress', count: 3 },
    { title: 'Submitted', count: 1 },
  ]
  return (
    <GuidePreviewFrame>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {cols.map((col) => (
          <Box
            key={col.title}
            sx={{
              flex: 1,
              minWidth: 72,
              p: 1,
              borderRadius: 1.5,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {col.title}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {col.count}
            </Typography>
          </Box>
        ))}
      </Box>
    </GuidePreviewFrame>
  )
}

function ContentReviewPreview() {
  return (
    <GuidePreviewFrame>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Reel draft · Alex Rivera
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Awaiting agency review
          </Typography>
        </Box>
        <Chip label="Release to client" size="small" color="primary" />
      </Box>
    </GuidePreviewFrame>
  )
}

function TaskListPreview() {
  const tasks = [
    { title: 'Instagram Reel', due: 'Due Jun 10' },
    { title: 'Story set (3)', due: 'Due Jun 14' },
  ]
  return (
    <GuidePreviewFrame>
      {tasks.map((task) => (
        <Box
          key={task.title}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 },
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {task.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {task.due}
            </Typography>
          </Box>
          <Chip label="Open" size="small" color="warning" variant="outlined" />
        </Box>
      ))}
    </GuidePreviewFrame>
  )
}

const QUEUE_VARIANT_BY_KEY = {
  clientList: 'clients',
  campaignList: 'campaigns',
  clientQueues: 'clientQueues',
  influencerQueues: 'influencerQueues',
}

/**
 * @param {{ previewKey?: string }} props
 */
export function GuidePreview({ previewKey }) {
  if (!previewKey) return null

  if (previewKey === 'actionCenter') return <ActionCenterPreview />
  if (previewKey === 'castingRoster') return <CastingRosterPreview />
  if (previewKey === 'phaseStripAm') return <PhaseStripPreview variant="am" />
  if (previewKey === 'phaseStripClient') return <PhaseStripPreview variant="client" />
  if (previewKey === 'pipelineColumns') return <PipelineColumnsPreview />
  if (previewKey === 'contentReview') return <ContentReviewPreview />
  if (previewKey === 'taskList') return <TaskListPreview />

  const queueVariant = QUEUE_VARIANT_BY_KEY[previewKey]
  if (queueVariant) return <QueueListPreview variant={queueVariant} />

  return null
}
